import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IAsset } from 'src/app/models/products/asset';
import { IBoundingBox } from 'src/app/models/boundingBox';
import { MetaData, MetaDataGroups } from 'src/app/models/assetMetaData';
import { AssetsService } from 'src/app/services/assets.service';
import { AssetMetaDataResult } from 'src/app/models/assets/assets-result';
import { AlertService } from 'src/app/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { WarningModalComponent } from '../../warning-modal/warning-modal.component';

@Component({
  selector: 'app-properties-view',
  templateUrl: './properties-view.component.html',
  styleUrls: ['./properties-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class PropertiesViewComponent implements OnInit, OnChanges {
  @Input() assetDetail!: IAsset;
  @Input() assetType = '';
  @Input() contentExtracting = false;
  @Output() boundingBoxProportionEmitter = new EventEmitter<IBoundingBox | null>();
  @Output() contentExtractingEmitter = new EventEmitter<boolean>();

  loading = false;
  noMetaData = false;
  metaDataGroups: MetaDataGroups[] = [];

  private readonly requiredKeys = [
    'SubjectTop',
    'SubjectLeft',
    'SubjectHeight',
    'SubjectWidth',
    'ImageWidth',
    'ImageHeight',
  ];

  constructor(
    private assetsService: AssetsService,
    private alertService: AlertService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.getAssetMetaData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const assetChange = changes['assetDetail'];
    if (assetChange && !assetChange.firstChange && assetChange.previousValue?.id !== assetChange.currentValue?.id) {
      this.getAssetMetaData();
    }
  }

  getAssetMetaData(): void {
    this.loading = true;
    this.assetsService.getAssetMetaData(this.assetDetail.id, this.assetType).subscribe((result: AssetMetaDataResult) => {
      this.metaDataGroups = [];
      if (result.assetMetaData?.metaData != null) {
        this.noMetaData = false;
        const groups = this.groupBy<MetaData>(result.assetMetaData.metaData, 'group');
        this.metaDataGroups = Object.keys(groups)
          .sort()
          .map((key) => ({
            group: key,
            metaData: new MatTableDataSource<MetaData>(groups[key]),
          }));
        this.boundingBoxProportionEmitter.emit(this.extractBoundingBox(result.assetMetaData.metaData));
      } else {
        this.noMetaData = true;
      }
      this.loading = false;
    });
  }

  extractBoundingBox(metaList: MetaData[]): IBoundingBox | null {
    const metaDict = metaList.reduce<Record<string, MetaData>>((acc, item) => {
      acc[item.key] = item;
      return acc;
    }, {});

    const hasAll = this.requiredKeys.every((k) => k in metaDict);
    if (!hasAll) {
      return null;
    }

    return {
      top: Number(metaDict['SubjectTop'].value) / Number(metaDict['ImageHeight'].value),
      left: Number(metaDict['SubjectLeft'].value) / Number(metaDict['ImageWidth'].value),
      height: Number(metaDict['SubjectHeight'].value) / Number(metaDict['ImageHeight'].value),
      width: Number(metaDict['SubjectWidth'].value) / Number(metaDict['ImageWidth'].value),
    };
  }

  generateExifData(): void {
    this.assetsService.generateAssetMetaData(this.assetDetail.id, this.assetType).subscribe();
  }

  extractAssetContent(): void {
    this.assetsService.extractLabelBoundingBox(this.assetDetail.id, this.assetType).subscribe({
      next: () => {
        this.alertService.successToast('Asset Content Queued for Extraction');
        this.contentExtracting = true;
        this.contentExtractingEmitter.emit(true);
      },
      error: () => {
        this.alertService.errorToast('Failed to extract asset content');
      },
    });
  }

  resetContentExtracting(): void {
    const dialogRef = this.dialog.open(WarningModalComponent, {
      data: {
        header: 'Reset Extraction',
        content: 'Cancel the current extraction and allow retry?',
        titleButtonOk: 'Reset',
        titleButtonNo: 'Cancel',
        showBtnNo: true,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed?.ok) {
        this.assetsService.resetAssetContentExtracting(this.assetDetail.id, this.assetType).subscribe({
          next: () => {
            this.contentExtracting = false;
            this.contentExtractingEmitter.emit(false);
          },
          error: () => {
            this.alertService.errorToast('Failed to reset extraction');
          },
        });
      }
    });
  }

  private groupBy<T>(collection: T[], key: keyof T): Record<string, T[]> {
    return collection.reduce((previous: Record<string, T[]>, current) => {
      const groupKey = String(current[key]);
      return {
        ...previous,
        [groupKey]: [...(previous[groupKey] || []), current],
      };
    }, {});
  }
}
